// =============================================================================
// FILE: packages/renderer/src/components/stealth/StealthToggle.tsx
// PURPOSE: Header button that activates the Stealth Workspace (Privacy Cloak).
//   On first use it prompts the operator to set a 4–6 digit PIN; thereafter one
//   click instantly cloaks the screen with a decoy document.
// =============================================================================

import { useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { EyeOff, ShieldCheck } from 'lucide-react';
import { useStealthStore, isValidPin } from '../../stores/stealth.store.js';
import { haptic } from '../../lib/soundManager.js';

export function StealthToggle(): JSX.Element {
  const { hasPin, setPin, activate } = useStealthStore();
  const [setup, setSetup] = useState(false);
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [err, setErr] = useState('');

  const onClick = (): void => {
    haptic(10);
    if (hasPin) { activate(); } else { setSetup(true); setErr(''); setPin1(''); setPin2(''); }
  };

  const confirmSetup = (): void => {
    if (!isValidPin(pin1)) { setErr('PIN must be 4–6 digits.'); return; }
    if (pin1 !== pin2) { setErr('PINs do not match.'); return; }
    setPin(pin1);
    setSetup(false);
    activate();
  };

  return (
    <>
      <button
        data-testid="stealth-mode-toggle"
        onClick={onClick}
        title="Privacy Mode — instantly hide your workspace behind a decoy"
        className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
        style={{ border: '1px solid rgba(148,163,184,0.35)', background: 'rgba(148,163,184,0.08)', color: 'rgba(203,213,225,0.9)' }}
      >
        <EyeOff className="h-3 w-3" strokeWidth={2} />
        Privacy
      </button>

      {setup && createPortal(
        <div onClick={() => setSetup(false)} style={{ position: 'fixed', inset: 0, zIndex: 2147483001, background: 'rgba(10,15,25,0.75)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="stealth-setup-modal"
            style={{ width: 'min(380px,92%)', borderRadius: 16, background: 'linear-gradient(180deg,#0b1622,#070d16)', border: '1px solid rgba(37,181,168,0.3)', padding: 24, textAlign: 'center' }}>
            <ShieldCheck style={{ width: 36, height: 36, color: '#5eead4', margin: '0 auto 10px' }} />
            <div style={{ color: '#e6fffb', fontSize: 16, fontWeight: 800 }}>Set a Privacy PIN</div>
            <div style={{ color: 'rgba(148,172,210,0.7)', fontSize: 12, margin: '5px 0 16px' }}>Choose a 4–6 digit PIN to unlock your workspace after cloaking. Your login email also works as a fallback.</div>
            <input data-testid="stealth-setup-pin1" autoFocus type="password" inputMode="numeric" value={pin1} onChange={(e) => { setPin1(e.target.value.replace(/\D/g, '')); setErr(''); }} placeholder="Enter PIN" maxLength={6}
              style={{ width: '100%', padding: '11px', borderRadius: 10, background: 'rgba(8,18,28,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#e2e8f0', fontSize: 15, textAlign: 'center', letterSpacing: 6 }} />
            <input data-testid="stealth-setup-pin2" type="password" inputMode="numeric" value={pin2} onChange={(e) => { setPin2(e.target.value.replace(/\D/g, '')); setErr(''); }} onKeyDown={(e) => { if (e.key === 'Enter') confirmSetup(); }} placeholder="Confirm PIN" maxLength={6}
              style={{ width: '100%', marginTop: 10, padding: '11px', borderRadius: 10, background: 'rgba(8,18,28,0.8)', border: '1px solid rgba(148,163,184,0.3)', color: '#e2e8f0', fontSize: 15, textAlign: 'center', letterSpacing: 6 }} />
            {err && <div data-testid="stealth-setup-error" style={{ color: '#fca5a5', fontSize: 11.5, marginTop: 8 }}>{err}</div>}
            <button data-testid="stealth-setup-confirm" onClick={confirmSetup}
              style={{ width: '100%', marginTop: 16, padding: 11, borderRadius: 10, background: '#2db5a8', border: 'none', color: '#04120f', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>Set PIN &amp; cloak now</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
